import * as d3 from 'd3';
import React from 'react';

import Axis, {defaultNumericTickFormat} from 'src/components/D3Axis';
import Legend, {LegendConfig, makeLine, makeRect} from 'src/components/Legend';
import {useTranslations} from 'src/components/TranslationsContext';
import {MARGIN} from 'src/components/Viz';
import WithWidth from 'src/components/WithWidth';
import {EvolutionBoxplotStat, ForecastEvolution} from 'src/lib/api/regionalReport';
import COLORS from 'src/lib/colors';
import {Dims} from 'src/lib/commonTypes';
import {getGraphDims} from 'src/lib/vizUtils';

const CHART_HEIGHT = 320;
const CHART_MARGIN = {...MARGIN, bottom: 50, top: 20, right: 20};
const HF_COLOR = COLORS.HF_MEAN;
const NWRFC_COLOR = COLORS.HF_WARNING;
const OBS_COLOR = COLORS.OBS;
const HIST_MEAN_COLOR = COLORS.AXIS;

type Props = {
  evolution: ForecastEvolution;
  historicalMeanMAF?: number;
};

function BoxplotChart({evolution, historicalMeanMAF, width}: Props & {width: number}) {
  const {t} = useTranslations();
  const {hf, nwrfc, observedToDate} = evolution;
  // "MM/DD" labels alone don't order across Jan 1 or Oct 1.
  const initTimeByDate = new Map([...hf, ...nwrfc].map((s) => [s.date, s.initTime]));
  // Saved demo fixtures predate initTime and fall back to their labels.
  const allDates = Array.from(initTimeByDate.keys()).sort((a, b) =>
    (initTimeByDate.get(a) ?? a).localeCompare(initTimeByDate.get(b) ?? b)
  );

  if (allDates.length === 0) {
    return <div className="text-sm text-gray-400 py-4">No forecast evolution data available.</div>;
  }

  const hfByDate: Record<string, EvolutionBoxplotStat> = {};
  hf.forEach((s) => (hfByDate[s.date] = s));
  const nwrfcByDate: Record<string, EvolutionBoxplotStat> = {};
  nwrfc.forEach((s) => (nwrfcByDate[s.date] = s));

  const dims: Dims = [width, CHART_HEIGHT];
  const [graphW, graphH] = getGraphDims(dims, CHART_MARGIN);

  const observedValues = allDates
    .map((d) => observedToDate[d])
    .filter((v): v is number => v != null && v > 0);

  // Single shared y-scale anchored at 0 so observed-to-date (small cumulative
  // values) and forecast boxes (full-season totals) read against the same
  // reference. Observed will sit near the x-axis early in the water year —
  // that's the accurate visual story.
  const allValues = [
    ...hf.flatMap((s) => [s.q05, s.q95]),
    ...nwrfc.flatMap((s) => [s.q05, s.q95]),
    ...observedValues,
  ];
  if (historicalMeanMAF != null) allValues.push(historicalMeanMAF);
  const yMin = 0;
  const yMax = (d3.max(allValues) || 1) * 1.08;

  const xScale = d3
    .scaleBand()
    .domain(allDates)
    .range([0, graphW])
    .paddingInner(0.3)
    .paddingOuter(0.15);

  const yScale = d3.scaleLinear().domain([yMin, yMax]).range([graphH, 0]).nice();

  const bandwidth = xScale.bandwidth();
  const boxW = bandwidth * 0.35;
  const offsetHF = -boxW / 2 - 1;
  const offsetNWRFC = boxW / 2 + 1;

  const observedLine = d3
    .line<string>()
    .defined((d) => observedToDate[d] != null && observedToDate[d] > 0)
    .x((d) => (xScale(d) ?? 0) + bandwidth / 2)
    .y((d) => yScale(observedToDate[d]));

  const lastObservedDate = [...allDates]
    .reverse()
    .find((d) => observedToDate[d] != null && observedToDate[d] > 0);
  const lastObservedValue = lastObservedDate != null ? observedToDate[lastObservedDate] : null;

  return (
    <svg width={dims[0]} height={dims[1]}>
      <g transform={`translate(${CHART_MARGIN.left},${CHART_MARGIN.top})`}>
        {/* Historical mean line */}
        {historicalMeanMAF != null && (
          <line
            x1={0}
            x2={graphW}
            y1={yScale(historicalMeanMAF)}
            y2={yScale(historicalMeanMAF)}
            stroke={HIST_MEAN_COLOR}
            strokeWidth={1.2}
            strokeDasharray="6,3"
            opacity={0.8}
          />
        )}

        {/* Boxplots */}
        {allDates.map((date) => {
          const cx = (xScale(date) ?? 0) + bandwidth / 2;
          return (
            <g key={date}>
              <title>
                {[
                  date,
                  ...[
                    {stat: hfByDate[date], source: 'HydroForecast'},
                    {stat: nwrfcByDate[date], source: 'NWRFC ESP'},
                  ]
                    .filter(({stat}) => stat != null)
                    .map(({stat, source}) =>
                      t('regionalReportEvolutionMeanValue', {
                        source,
                        volume: stat.median.toFixed(1),
                        unit: evolution.unit,
                      })
                    ),
                ].join(' · ')}
              </title>
              {hfByDate[date] && (
                <Boxplot
                  cx={cx + offsetHF}
                  stat={hfByDate[date]}
                  yScale={yScale}
                  boxW={boxW}
                  color={HF_COLOR}
                />
              )}
              {nwrfcByDate[date] && (
                <Boxplot
                  cx={cx + offsetNWRFC}
                  stat={nwrfcByDate[date]}
                  yScale={yScale}
                  boxW={boxW}
                  color={NWRFC_COLOR}
                />
              )}
              {historicalMeanMAF != null && historicalMeanMAF > 0 && (
                <text
                  x={cx}
                  y={yScale(Math.max(hfByDate[date]?.q95 ?? 0, nwrfcByDate[date]?.q95 ?? 0)) - 4}
                  textAnchor="middle"
                  fontSize={8}
                  fill="#374151"
                >
                  {hfByDate[date] &&
                    `${Math.round((hfByDate[date].median / historicalMeanMAF) * 100)}%`}
                </text>
              )}
            </g>
          );
        })}

        {/* Observed-to-date trendline with dot markers on the shared y-scale. */}
        <path d={observedLine(allDates) || ''} fill="none" stroke={OBS_COLOR} strokeWidth={2} />
        {allDates.map((d) =>
          observedToDate[d] != null && observedToDate[d] > 0 ? (
            <circle
              key={`obs-${d}`}
              cx={(xScale(d) ?? 0) + bandwidth / 2}
              cy={yScale(observedToDate[d])}
              r={2.5}
              fill={OBS_COLOR}
              stroke="white"
              strokeWidth={0.75}
            />
          ) : null
        )}
        {lastObservedDate != null && lastObservedValue != null && (
          <text
            x={(xScale(lastObservedDate) ?? 0) + bandwidth / 2 - 6}
            textAnchor="end"
            y={yScale(lastObservedValue) - 4}
            fontSize={9}
            fill={OBS_COLOR}
            fontWeight={600}
          >
            {lastObservedValue.toFixed(1)} MAF observed through {lastObservedDate}
          </text>
        )}

        <Axis
          axisCreator={d3.axisBottom(xScale).tickSizeOuter(0)}
          transform={`translate(0,${graphH})`}
        />
        <Axis
          axisCreator={d3
            .axisLeft(yScale)
            .ticks(6)
            .tickFormat((d) => defaultNumericTickFormat(d.valueOf()))}
          yLabelProps={{
            graphDims: [graphW, graphH],
            text: `${evolution.targetPeriod} volume${evolution.siteName ? ` at ${evolution.siteName}` : ''} (MAF)`,
          }}
        />

        <text x={graphW / 2} y={graphH + 40} textAnchor="middle" fontSize={11} fill="#6b7280">
          Forecast Issue Date
        </text>
      </g>
    </svg>
  );
}

function Boxplot({
  cx,
  stat,
  yScale,
  boxW,
  color,
}: {
  cx: number;
  stat: EvolutionBoxplotStat;
  yScale: d3.ScaleLinear<number, number>;
  boxW: number;
  color: string;
}) {
  const halfW = boxW / 2;
  const whiskerW = boxW / 3;
  return (
    <g>
      <line
        x1={cx}
        x2={cx}
        y1={yScale(stat.q95)}
        y2={yScale(stat.q05)}
        stroke={color}
        strokeWidth={1}
      />
      <line
        x1={cx - whiskerW}
        x2={cx + whiskerW}
        y1={yScale(stat.q95)}
        y2={yScale(stat.q95)}
        stroke={color}
        strokeWidth={1}
      />
      <line
        x1={cx - whiskerW}
        x2={cx + whiskerW}
        y1={yScale(stat.q05)}
        y2={yScale(stat.q05)}
        stroke={color}
        strokeWidth={1}
      />
      <rect
        x={cx - halfW}
        y={yScale(stat.q75)}
        width={boxW}
        height={yScale(stat.q25) - yScale(stat.q75)}
        fill={color}
        fillOpacity={0.5}
        stroke={color}
        strokeWidth={1}
      />
      <line
        x1={cx - halfW}
        x2={cx + halfW}
        y1={yScale(stat.median)}
        y2={yScale(stat.median)}
        stroke={color}
        strokeWidth={2}
      />
    </g>
  );
}

export function StaticLegend({configs}: {configs: LegendConfig[]}) {
  const visibleById = React.useMemo(
    () => Object.fromEntries(configs.map((c) => [c.id, true])),
    [configs]
  );
  const noop = React.useCallback(() => {}, []);
  return (
    <div style={{pointerEvents: 'none'}}>
      <Legend configs={configs} isVisibleById={visibleById} onChange={noop} />
    </div>
  );
}

export default function ForecastEvolutionChart({evolution, historicalMeanMAF}: Props) {
  const {t} = useTranslations();
  const hasData = evolution.hf.length > 0 || evolution.nwrfc.length > 0;
  if (!hasData) return null;

  const legendConfigs: LegendConfig[] = [
    {
      id: 'hf',
      display: t('regionalReportHfMean'),
      makeIcon: () => makeRect({fill: HF_COLOR, fillOpacity: 0.5, stroke: HF_COLOR}),
    },
    {
      id: 'nwrfc',
      display: t('regionalReportNwrfcMean'),
      makeIcon: () => makeRect({fill: NWRFC_COLOR, fillOpacity: 0.5, stroke: NWRFC_COLOR}),
    },
    {
      id: 'marks',
      display: t('regionalReportEvolutionMarks'),
      makeIcon: () => makeRect({fill: 'none', stroke: '#6b7280'}),
    },
    ...(Object.values(evolution.observedToDate).some((v) => v != null && v > 0)
      ? [
          {
            id: 'observed',
            display: 'Observed to Date',
            makeIcon: () => makeLine({stroke: OBS_COLOR, strokeWidth: 2}),
          } satisfies LegendConfig,
        ]
      : []),
    ...(historicalMeanMAF != null
      ? [
          {
            id: 'histMean',
            display: 'Historical Mean',
            makeIcon: () =>
              makeLine({stroke: HIST_MEAN_COLOR, strokeWidth: 1.5, strokeDasharray: '6 3'}),
          } satisfies LegendConfig,
        ]
      : []),
  ];

  const latestMeans = [
    {stats: evolution.hf, source: 'HydroForecast', color: HF_COLOR},
    {stats: evolution.nwrfc, source: 'NWRFC ESP', color: NWRFC_COLOR},
  ].flatMap(({stats, source, color}) => {
    const latest = stats.at(-1);
    return latest ? [{source, color, date: latest.date, volume: latest.median}] : [];
  });

  return (
    <div>
      <WithWidth>
        {(width) => (
          <BoxplotChart evolution={evolution} historicalMeanMAF={historicalMeanMAF} width={width} />
        )}
      </WithWidth>
      <StaticLegend configs={legendConfigs} />
      <div className="mt-3 text-xs">
        <p className="font-semibold text-gray-600">{t('regionalReportLatestMeans')}</p>
        <dl className="mt-1 flex flex-wrap gap-x-6 gap-y-2">
          {latestMeans.map(({source, color, date, volume}) => (
            <div key={source}>
              <dt className="font-semibold" style={{color}}>
                {source}
              </dt>
              <dd>
                {t('regionalReportEvolutionMean', {
                  volume: volume.toFixed(1),
                  unit: evolution.unit,
                })}{' '}
                · {date}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
